import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import { Button, Flex, Icon } from '@chakra-ui/react';
import {
  ListingComparableSetFragment,
  ListingComparableSetSource,
  MlsListingFragment,
} from '@client/graphql/__generated__/types';
import { debounce } from 'lodash';
import { StarIcon } from 'lucide-react';
import { LngLatBounds, MapEvent } from 'mapbox-gl';
import { decode, encode } from 'ngeohash';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Map,
  NavigationControl,
  Popup,
  ScaleControl,
  ViewStateChangeEvent,
} from 'react-map-gl';
import { TextMarker } from '~/apps/consumer/components/Map/MapMarkers/TextMarker';
import { DEFAULT_POSITION } from '~/apps/consumer/components/MapSearch/constants';
import { useExploreContext } from '~/apps/consumer/components/MapSearch/ExploreContext';
import { ExploreMapMarker } from '~/apps/consumer/components/MapSearch/ExploreMap/ExploreMapMarker';
import { convertBoundsToBbox } from '~/apps/consumer/components/MapSearch/helpers';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';
import {
  GEO_HASH_PRECISION,
  getListingPrice,
  groupListingsByGeo,
} from '~/apps/consumer/utils/listing.utils';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { getInitialCompsBBox } from '../../../BuyerOfferAnalysis/Comps/utils';
import { GroupedListingCard } from '../../../Listings/ListingCard/GroupedListingCard/GroupedListingCard';
import { HorizontalListingCard } from '../../../Listings/ListingCard/HorizontalListingCard';
import { NormalizedPropertyDetails } from '../../../Listings/ListingPage/utils';
import { EditCompsListingCard } from '../EditCompsListingCard';

export const EditCompsMap = ({
  mainListing,
  comparableSet,
  comps,
  setComps,
  initialSelectedComp,
  setInitialSelectedComp,
}: {
  mainListing: NormalizedPropertyDetails;
  comparableSet?: ListingComparableSetFragment;
  comps: ComparableState[];
  setComps: (comps: ComparableState[]) => void;
  initialSelectedComp?: ComparableState;
  setInitialSelectedComp?: (comp?: ComparableState | undefined) => void;
}) => {
  const isMobile = useIsMobile();

  const {
    listings,
    updateFilters,
    selectedListing,
    setSelectedListing,
    loading,
    mapRef,
  } = useExploreContext();

  useEffect(() => {
    if (initialSelectedComp && !loading && listings?.length > 0) {
      setSelectedListing([initialSelectedComp]);
      if (setInitialSelectedComp) {
        setInitialSelectedComp(undefined);
      }
    }
  }, [
    initialSelectedComp,
    setSelectedListing,
    setInitialSelectedComp,
    loading,
    listings,
  ]);

  useEffect(() => {
    if (
      selectedListing?.length === 1 &&
      selectedListing?.[0]?.mlsListing?.id === mainListing.mlsListingId
    ) {
      setSelectedListing([]);
    }
  }, [selectedListing, setSelectedListing, mainListing]);

  const initialBBox = getInitialCompsBBox(comparableSet, mainListing);
  const initialBounds = initialBBox
    ? new LngLatBounds([
        initialBBox[0],
        initialBBox[1],
        initialBBox[2],
        initialBBox[3],
      ])
    : undefined;

  const fetchListings = useCallback(
    (event: MapEvent) => {
      const bounds = event.target.getBounds();
      const bbox = convertBoundsToBbox(bounds);

      if (bbox) {
        updateFilters({
          bbox,
        });
      }
    },
    [updateFilters]
  );
  const mainListingGeoHash = useMemo(
    () =>
      mainListing?.location
        ? encode(
            mainListing.location.lat,
            mainListing.location.lon,
            GEO_HASH_PRECISION
          )
        : null,
    [mainListing]
  );

  const handleMove = useMemo(
    () =>
      debounce((event: ViewStateChangeEvent) => {
        fetchListings(event);
      }, 300),
    [fetchListings]
  );

  const markers = useMemo(() => {
    const filteredListings = [
      ...(listings?.filter(({ mlsListing }) => {
        // filter out main listing and comps
        return (
          mlsListing.id !== mainListing.mlsListingId &&
          !comps?.some(({ mlsListing: comp }) => comp.id === mlsListing.id)
        );
      }) ?? []),
      ...comps, // add back in comps to keep unique set
    ];
    const groupedListings = groupListingsByGeo(filteredListings);

    const someGroupHasMainListing =
      !!mainListingGeoHash && mainListingGeoHash in groupedListings;

    const mainMarker =
      mainListing?.location && !someGroupHasMainListing ? (
        <ExploreMapMarker
          key={`marker-${mainListing.id}-main`}
          lat={mainListing.location.lat}
          lng={mainListing.location.lon}
          markerOverride={
            <TextMarker
              text={
                <Flex alignItems="center" gap={1}>
                  <Icon as={StarIcon} />
                  This home
                </Flex>
              }
            />
          }
          zIndex={2}
          zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
        />
      ) : null;

    const otherMarkers = Object.entries(groupedListings).map(
      ([geoHash, group]) => {
        if (group.length === 0) {
          return null;
        }

        const groupContainsComp = group.some(({ mlsListing }) => {
          return comps?.some(
            ({ mlsListing: comp }) => comp.id === mlsListing.id
          );
        });

        const isMainListingInGroup = geoHash === mainListingGeoHash;

        const markerOverride = isMainListingInGroup ? (
          <TextMarker
            text={
              <Flex alignItems="center" gap={1}>
                <Icon as={StarIcon} />
                This home + {group.length} listings
              </Flex>
            }
          />
        ) : undefined;

        const latLng = decode(geoHash);

        const lat = latLng.latitude;
        const lng = latLng.longitude;

        if (group.length === 1) {
          const { mlsListing } = group[0];

          if (
            !mlsListing.geo?.location?.lat ||
            !mlsListing.geo?.location?.lon
          ) {
            return null;
          }
          const price = getListingPrice(mlsListing);

          return (
            <ExploreMapMarker
              key={`marker-${mlsListing.id}-comp`}
              isPremium={groupContainsComp}
              lat={isMainListingInGroup ? lat : mlsListing.geo.location.lat}
              lng={isMainListingInGroup ? lng : mlsListing.geo.location.lon}
              markerOverride={markerOverride}
              price={price}
              zIndex={isMainListingInGroup ? 2 : undefined}
              zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
              onClick={(event) => {
                event.originalEvent.stopPropagation();
                setSelectedListing([
                  {
                    mlsListing: mlsListing,
                  },
                ]);
              }}
            />
          );
        }

        return (
          <ExploreMapMarker
            key={`marker-${group
              .map(({ mlsListing }) => mlsListing.id)
              .join('-')}`}
            isGrouped
            bgOverride={groupContainsComp ? 'purpleGradient' : 'gray.500'}
            colorOverride="white"
            groupCount={group.length}
            lat={lat}
            lng={lng}
            markerOverride={markerOverride}
            zIndex={isMainListingInGroup ? 2 : groupContainsComp ? 1 : 0}
            zoom={mapRef.current?.getZoom() || DEFAULT_POSITION.zoom}
            onClick={(event) => {
              event.originalEvent.stopPropagation();
              setSelectedListing(group);
            }}
          />
        );
      }
    );

    return [mainMarker, ...otherMarkers];
  }, [
    mainListing,
    mapRef,
    listings,
    setSelectedListing,
    comps,
    mainListingGeoHash,
  ]);

  const getIsMlsListingAdded = useCallback(
    (mlsListing: MlsListingFragment) => {
      return comparableSet?.listingComparables?.some(
        (comparable) => comparable.mlsListing.id === mlsListing?.id
      );
    },
    [comparableSet]
  );

  const handleAddRemove = (mlsListing: MlsListingFragment) => {
    if (!mlsListing) {
      return;
    }

    const isSelectedListingAdded = comparableSet?.listingComparables?.some(
      (comparable) => comparable.mlsListing.id === mlsListing?.id
    );

    let newComps = [] as ComparableState[];
    if (isSelectedListingAdded) {
      newComps = comps?.filter((comp) => comp.mlsListing.id !== mlsListing?.id);
    } else {
      newComps = [
        ...comps,
        {
          mlsListing,
          source: ListingComparableSetSource.AGENT,
        },
      ];
    }

    setComps(newComps);
  };

  const popup = () => {
    if (!selectedListing?.length) {
      return null;
    }

    const geoHash = encode(
      selectedListing[0].mlsListing.geo.location.lat,
      selectedListing[0].mlsListing.geo.location.lon,
      GEO_HASH_PRECISION
    );

    const isMainListingInGroup = geoHash === mainListingGeoHash;

    const hashLatLng = decode(geoHash);
    const lat = hashLatLng.latitude;
    const lng = hashLatLng.longitude;

    if (selectedListing?.length === 1 && selectedListing?.[0]?.mlsListing) {
      return (
        <Popup
          latitude={
            isMainListingInGroup
              ? lat
              : selectedListing[0].mlsListing.geo.location.lat
          }
          longitude={
            isMainListingInGroup
              ? lng
              : selectedListing[0].mlsListing.geo.location.lon
          }
          offset={15}
          style={{
            maxWidth: '260px',
            minWidth: '260px',
            zIndex: 2,
            borderRadius: '12px',
          }}
          onClose={() => setSelectedListing([])}
        >
          {isMobile ? (
            <HorizontalListingCard
              bottomButton={
                <Button
                  size="xs"
                  width="100%"
                  onClick={() => handleAddRemove(selectedListing[0].mlsListing)}
                >
                  {getIsMlsListingAdded(selectedListing[0].mlsListing)
                    ? 'Remove Comp'
                    : 'Add Comp'}
                </Button>
              }
              isHoverable={false}
              mlsListing={selectedListing[0].mlsListing}
            />
          ) : (
            <EditCompsListingCard
              isAdded={getIsMlsListingAdded(selectedListing[0].mlsListing)}
              mlsListing={selectedListing[0].mlsListing}
              onClick={() => handleAddRemove(selectedListing[0].mlsListing)}
            />
          )}
        </Popup>
      );
    }

    if ((selectedListing?.length || 0) > 1) {
      return (
        <Popup
          latitude={lat}
          longitude={lng}
          offset={15}
          style={{
            maxWidth: '350px',
            minWidth: '300px',
            zIndex: 2,
            borderRadius: '12px',
          }}
          onClose={() => setSelectedListing([])}
        >
          <GroupedListingCard
            listings={selectedListing}
            rightButton={(listing) => (
              <Flex alignItems="center" height="100%" justifyContent="center">
                <Button
                  size="xs"
                  onClick={() => handleAddRemove(listing.mlsListing)}
                >
                  {getIsMlsListingAdded(listing.mlsListing)
                    ? 'Remove Comp'
                    : 'Add Comp'}
                </Button>
              </Flex>
            )}
          />
        </Popup>
      );
    }
  };

  return (
    <Flex
      direction={{ base: 'column-reverse', xl: 'column' }}
      flexBasis={0}
      flexGrow={1}
      height="100%"
    >
      <Map
        ref={mapRef}
        initialViewState={{
          bounds: initialBounds,
          longitude: mainListing?.location?.lon,
          latitude: mainListing?.location?.lat,
          zoom: 12,
        }}
        mapStyle="mapbox://styles/mapbox/navigation-guidance-night-v4"
        mapboxAccessToken={window.ENVS.mapboxApiKey}
        minZoom={3}
        padding={
          isMobile
            ? { top: 70, right: 0, bottom: 0, left: 0 }
            : { top: 50, right: 300, bottom: 0, left: 0 }
        }
        scrollZoom={false}
        style={{ borderRadius: isMobile ? '0' : '8px' }}
        testMode={window.ENVS.environment === 'e2e'}
        onLoad={fetchListings}
        onMove={handleMove}
      >
        <NavigationControl position="bottom-left" />
        <ScaleControl />
        {markers}
        {popup()}
      </Map>
    </Flex>
  );
};
